/**
 * Gráfico de Fluxo Semanal (Últimos 7 Dias)
 * Mostra o fluxo de pessoas detectadas nos últimos 7 dias,
 * com comparativo opcional do mesmo período do ano anterior (dados históricos).
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Calendar, TrendingUp, TrendingDown, Users, Star, Minus } from 'lucide-react';

interface DayObservation {
  id: string;
  date: string;
  title: string;
  description: string | null;
  category: string;
  color: string;
}

export interface WeeklyFlowData {
  date: string;
  day_name: string;
  day_label?: string;
  people_count: number | null;
  detections_count: number | null;
  status?: 'consolidated' | 'pending' | 'unavailable' | 'in_progress';
  prev_year_count?: number | null;
  prev_year_date?: string;
}

interface WeeklyFlowChartProps {
  data?: WeeklyFlowData[];
  observations?: DayObservation[];
  className?: string;
  onAddObservation?: (date: string) => void;
}

export const WeeklyFlowChart: React.FC<WeeklyFlowChartProps> = ({
  data = [],
  observations = [],
  className = '',
  onAddObservation,
}) => {
  // Determinar se há dados do ano anterior disponíveis
  const hasPrevYear = React.useMemo(
    () => data.some(d => (d.prev_year_count ?? 0) > 0),
    [data]
  );
  const incomplete = data.some(d => d.people_count === null || d.status === 'pending' || d.status === 'unavailable');
  const chartData = data.map(d => d.status === 'pending' || d.status === 'unavailable'
    ? { ...d, people_count: null, detections_count: null } : d);
  // Calcular estatísticas
  const statistics = React.useMemo(() => {
    if (data.length === 0) {
      return { total: 0, average: 0, maxDay: { day_name: '', people_count: 0 }, prevYearTotal: 0, diffPct: null as number | null };
    }

    const available = data.filter(d => d.people_count !== null && d.status !== 'pending' && d.status !== 'unavailable');
    const total = available.reduce((sum, d) => sum + (d.people_count ?? 0), 0);
    const average = available.length ? Math.round(total / available.length) : 0;
    const maxDay = available.reduce((max, d) => ((d.people_count ?? 0) > (max.people_count ?? 0) ? d : max),
      available[0] ?? { day_name: '', people_count: 0 });
    const prevYearTotal = data.reduce((sum, d) => sum + (d.prev_year_count ?? 0), 0);
    const previousYearComplete = data.every(d => d.prev_year_count !== null && d.prev_year_count !== undefined);
    const diffPct = !incomplete && previousYearComplete && prevYearTotal > 0
      ? Math.round(((total - prevYearTotal) / prevYearTotal) * 100)
      : null;

    return { total, average, maxDay, prevYearTotal, diffPct };
  }, [data, incomplete]);

  // Mapear observações por data para acesso rápido
  const observationsByDate = React.useMemo(() => {
    const map: Record<string, DayObservation[]> = {};
    observations.forEach(obs => {
      if (!map[obs.date]) map[obs.date] = [];
      map[obs.date].push(obs);
    });
    return map;
  }, [observations]);

  // Cores personalizadas para cada barra
  const getBarColor = (index: number) => {
    const colors = [
      '#3b82f6', // Azul
      '#f97316', // Laranja    (era emerald — conflitava com teal do ano anterior)
      '#8b5cf6', // Roxo
      '#f59e0b', // Âmbar
      '#ef4444', // Vermelho
      '#84cc16', // Lima       (era cyan — conflitava com teal do ano anterior)
      '#ec4899', // Pink
    ];
    return colors[index % colors.length];
  };

  // Legenda customizada com cor adaptável ao tema
  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex justify-center gap-5 pt-5 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-600 rounded"></div>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {hasPrevYear ? `${new Date(data[0]?.date + 'T12:00:00').getFullYear()} (atual)` : 'Pessoas Detectadas'}
          </span>
        </div>
        {hasPrevYear && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-teal-400 rounded"></div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {new Date(data[0]?.prev_year_date + 'T12:00:00').getFullYear()} (histórico)
            </span>
          </div>
        )}
      </div>
    );
  };

  // Tick customizado para o eixo X (destaca dia atual)
  const CustomAxisTick = ({ x, y, payload }: any) => {
    if (!payload?.value) return <g />;
    
    // Verificar se é o dia atual
    const today = new Date();
    const todayStr = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}`;
    const isToday = payload.value.includes(todayStr);
    
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill={isToday ? '#10b981' : '#6b7280'}
          fontSize={12}
          fontWeight={isToday ? 'bold' : 'normal'}
          transform="rotate(-45)"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  // Tooltip customizado
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      const [year, month, day] = d.date.split('-').map(Number);
      const displayDate = new Date(year, month - 1, day);
      const dayObs = observationsByDate[d.date] || [];
      const prevCount: number = d.prev_year_count ?? 0;
      const prevAvailable = d.prev_year_count !== null && d.prev_year_count !== undefined;
      const delta = prevAvailable && d.people_count !== null && prevCount > 0
        ? Math.round(((d.people_count - prevCount) / prevCount) * 100)
        : null;
      
      return (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-w-xs">
          <p className="font-semibold text-gray-900 dark:text-white mb-2">{d.day_name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Data: <span className="font-medium">{displayDate.toLocaleDateString('pt-BR')}</span>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Pessoas: <span className="font-medium text-purple-600 dark:text-purple-400">{d.people_count?.toLocaleString('pt-BR') ?? 'Indisponível'}</span>
          </p>
          {hasPrevYear && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ano anterior: <span className="font-medium text-teal-500">{prevAvailable ? prevCount.toLocaleString('pt-BR') : 'Indisponível'}</span>
              {delta !== null && (
                <span className={`ml-2 font-semibold ${delta >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  ({delta >= 0 ? '+' : ''}{delta}%)
                </span>
              )}
            </p>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Detecções: <span className="font-medium">{d.detections_count}</span>
          </p>
          {dayObs.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
              {dayObs.map((obs: DayObservation) => (
                <div key={obs.id} className="flex items-start gap-1.5 mt-1">
                  <Star className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: obs.color }} />
                  <div>
                    <p className="text-xs font-semibold" style={{ color: obs.color }}>{obs.title}</p>
                    {obs.description && <p className="text-xs text-gray-500 dark:text-gray-400">{obs.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-6 h-6 text-blue-600" />
          Fluxo dos Últimos 7 Dias
          {hasPrevYear && (
            <span className="text-xs font-normal bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 px-2 py-0.5 rounded-full ml-1">
              + comparativo ano anterior
            </span>
          )}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Evolução diária do fluxo de pessoas
              {hasPrevYear && ' — barras teal = mesmo período do ano passado (histórico importado)'}
        </p>
      </div>

      {/* Estatísticas Resumidas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
          <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1 flex items-center gap-1">
            <Users className="w-4 h-4" />
            {incomplete ? 'Total parcial (dados incompletos)' : 'Total (7 dias, hoje em andamento)'}
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {statistics.total.toLocaleString('pt-BR')}
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Média: {incomplete ? 'Indisponível' : `${statistics.average.toLocaleString('pt-BR')}/dia`}
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-700">
          <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1 flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            Dia de Maior Fluxo
          </div>
          <div className="text-lg font-bold text-green-900 dark:text-green-100">
            {incomplete ? 'Indisponível' : statistics.maxDay.day_name || 'N/A'}
          </div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
            {!incomplete && `${(statistics.maxDay.people_count ?? 0).toLocaleString('pt-BR')} pessoas`}
          </div>
        </div>

        {/* Terceiro card: comparativo ano anterior (quando disponível) ou média diária */}
        {hasPrevYear && statistics.diffPct !== null ? (
          <div className={`bg-gradient-to-br rounded-xl p-4 border ${
            statistics.diffPct >= 0
              ? 'from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-700'
              : 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200 dark:border-red-700'
          }`}>
            <div className={`text-sm font-medium mb-1 flex items-center gap-1 ${
              statistics.diffPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
            }`}>
              {statistics.diffPct > 0
                ? <TrendingUp className="w-4 h-4" />
                : statistics.diffPct < 0
                ? <TrendingDown className="w-4 h-4" />
                : <Minus className="w-4 h-4" />}
              vs Ano Anterior
            </div>
            <div className={`text-2xl font-bold ${
              statistics.diffPct >= 0 ? 'text-emerald-900 dark:text-emerald-100' : 'text-red-900 dark:text-red-100'
            }`}>
              {statistics.diffPct >= 0 ? '+' : ''}{statistics.diffPct}%
            </div>
            <div className={`text-xs mt-1 ${
              statistics.diffPct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
            }`}>
              Ano anterior: {statistics.prevYearTotal.toLocaleString('pt-BR')} pessoas
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
            <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">
              Média Diária
            </div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {incomplete ? 'Indisponível' : statistics.average.toLocaleString('pt-BR')}
            </div>
            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
              pessoas/dia
            </div>
          </div>
        )}
      </div>

      <ul className="text-sm text-amber-700 dark:text-amber-300 mb-3" aria-label="Disponibilidade dos dados">
        {data.filter(d => d.status && d.status !== 'consolidated').map(d => (
          <li key={d.date}>{d.day_label ?? d.date}: {d.status === 'pending' ? 'Consolidação pendente' : d.status === 'in_progress' ? 'Dia em andamento' : 'Dados indisponíveis'}</li>
        ))}
      </ul>
      {/* Gráfico */}
      {data.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum dado disponível</p>
            <p className="text-sm mt-1">Aguardando detecções dos últimos 7 dias</p>
          </div>
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              barCategoryGap="25%"
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis
                dataKey="day_label"
                tick={<CustomAxisTick />}
                height={80}
              />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              {/* Barra do ano atual */}
              <Bar dataKey="people_count" name="Pessoas Detectadas" radius={[8, 8, 0, 0]}>
                {data.map((entry, index) => {
                  const hasObs = observationsByDate[entry.date]?.length > 0;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={hasObs ? (observationsByDate[entry.date][0].color) : (hasPrevYear ? '#8b5cf6' : getBarColor(index))}
                      stroke={hasObs ? observationsByDate[entry.date][0].color : undefined}
                      strokeWidth={hasObs ? 3 : 0}
                    />
                  );
                })}
              </Bar>
              {/* Barra do ano anterior (só quando há dados históricos) */}
              {hasPrevYear && (
                <Bar
                  dataKey="prev_year_count"
                  name="Ano anterior"
                  radius={[6, 6, 0, 0]}
                  fill="#14b8a6"
                  opacity={0.75}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Observações do período */}
      {(() => {
        const visibleObs = observations.filter(obs => data.some(d => d.date === obs.date));
        return visibleObs.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
              <Star className="w-4 h-4 text-amber-500" />
              Eventos no período
            </p>
            <div className="flex flex-wrap gap-2">
              {visibleObs.map(obs => (
                <span
                  key={obs.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: obs.color }}
                  title={obs.description || obs.title}
                >
                  {obs.title}
                  <span className="opacity-75">({new Date(obs.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })})</span>
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Insights */}
      {!incomplete && data.length > 0 && (statistics.maxDay.people_count ?? 0) > 0 && (
        <div className="mt-4 p-4 rounded-lg border bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
          <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
            💡 Insight: O dia com maior movimento foi <span className="font-bold">{statistics.maxDay.day_name}</span> com{' '}
            <span className="font-bold">{(statistics.maxDay.people_count ?? 0).toLocaleString('pt-BR')}</span> pessoas detectadas.
            {(statistics.maxDay.people_count ?? 0) > statistics.average * 1.5 &&
              ' Este dia teve um fluxo significativamente acima da média - considere investigar eventos especiais.'}
          </p>
        </div>
      )}
    </div>
  );
};
